import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useTable, useSortBy, useFilters, useGlobalFilter, Column, HeaderGroup, ColumnInstance, UseSortByColumnProps } from 'react-table';
import './RealTimeDataTable.scss';
import Loader from '../../features/common/Loader/Loader';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import { formatDate } from '../../utilities/dateHelpers';

export interface ColumnFields {
  name: string;
  displayOrder: string;
  units: string;
  shortName?: string;
  isVisible: boolean;
}

interface RealTimeDataTableProps {
  searchTerm: string;
  data: any[];
  columns: ColumnFields[];
  loading?: boolean;
  onFilteredDataReady?: (filtered: any[]) => void;
  source?: string;
}

type ExtendedColumn = Column<any> & {
  getSortByToggleProps?: () => any;
  isSorted?: boolean;
  isSortedDesc?: boolean;
  canFilter?: boolean;
  render?: (type: string) => any;
};

const RealTimeDataTable: React.FC<RealTimeDataTableProps> = React.memo(
  ({ searchTerm, data, columns, loading, onFilteredDataReady, source }) => {
    // Filter state management (similar to WellNotesDataTable)
    const [currentFilters, setCurrentFilters] = useState<unknown[]>([]);
    const currentFiltersRef = useRef(currentFilters);

    useEffect(() => {
      currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
    }, [currentFilters]);

    // Create multi-condition filter with useMemo to prevent recreation
    const multiConditionFilter = useMemo(() => createMultiConditionFilter(setCurrentFilters, currentFiltersRef, true), []);
    const clearFilterForColumn = useCallback((columnId: string) => {
      setCurrentFilters((prev) =>
        prev.filter((filter: unknown) => (filter as { columnId: string }).columnId !== columnId),
      );
    }, []);

    const dateHeader = source == 'dailyAverage' ? 'Date' : 'Date / Time';
    const tableColumns: ExtendedColumn[] = useMemo(() => {
      return columns.map((col) => ({
        Header: () => (
          <div className='rt-header-container' title={col.name == 'date' ? dateHeader : col.name}>
            <div>{col.name == 'date' ? dateHeader : col.shortName}</div>
            {col.units && <div className='rt-header-unit'>({col.units})</div>}
          </div>
        ),
        id: col.name,
        accessor: col.name,
        filter: multiConditionFilter,
        Cell: ({ value }) => {
          if (col.name.toLowerCase() === 'date' && value) {
            const formatted = formatDate(value, source !== 'dailyAverage');
            return formatted !== '-' ? formatted : '-';
          }
          if (!isNaN(Number(value)) && value !== null && Number(value) % 1 !== 0) {
            const preciseValue = Math.floor(value * 1000) / 1000;
            return preciseValue.toFixed(3).replace(/\.?0+$/, '');
          }
          return value ?? '-';
        },
        sortType: (rowA: { values: Record<string, any> }, rowB: { values: Record<string, any> }) => {
          const valueA = rowA.values[col.name] ?? '';
          const valueB = rowB.values[col.name] ?? '';
          const numA = !isNaN(Number(valueA)) ? Number(valueA) : valueA;
          const numB = !isNaN(Number(valueB)) ? Number(valueB) : valueB;
          if (typeof numA === 'number' && typeof numB === 'number') {
            return numA - numB;
          }
          return numA.toString().localeCompare(numB.toString());
        },
      }));
    }, [columns, multiConditionFilter, source]);

    const defaultColumn: any = useMemo(
      () => ({
        // Let's set up our default Filter UI
        Filter: (props: any) => (
          <DefaultColumnFilter
            {...props}
            clearFilterForColumn={clearFilterForColumn}
            currentFiltersRef={currentFiltersRef}
          />
        ),
      }),
      [clearFilterForColumn, currentFiltersRef],
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setGlobalFilter } = useTable(
      {
        columns: tableColumns,
        data: data,
        defaultColumn,
        initialState: { sortBy: [{ id: columns[0]?.name || '', desc: false }] } as any,
      },
      useFilters,
      useGlobalFilter,
      useSortBy,
    ) as any;

    // Apply global search filter
    useEffect(() => {
      setGlobalFilter(searchTerm);
    }, [searchTerm, setGlobalFilter]);

    useEffect(() => {
      if (onFilteredDataReady) {
        onFilteredDataReady(
          rows.map((row: any) => {
            prepareRow(row);
            return row.original;
          }),
        );
      }
    }, [rows, onFilteredDataReady]);

    return (
      <section className='table-container-wrapper'>
        <div className='table-container'>
          <div className='rt-table-scroll'>
            <table {...getTableProps()} className='data-table rt-detail-table'>
              <thead>
                {headerGroups.map((headerGroup: HeaderGroup<any>) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                    {headerGroup.headers.map((col) => {
                      const column = col as unknown as ColumnInstance<any> & UseSortByColumnProps<any> as any;
                      return (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className='rt-header-cell'
                          key={column.id}
                        >
                          <div className='rt-detail'>
                            <span className='rt-header-title'>{column.render('Header')}</span>
                            {column.isSorted && (
                              <span
                                className={`tdt-sort-icon ${column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'}`}
                                style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                              />
                            )}
                            <span>{column.canFilter ? column.render('Filter') : null}</span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              {!loading && (
                <tbody {...getTableBodyProps()}>
                  {rows.map((row: any) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell: any) => (
                          <td {...cell.getCellProps()} style={{ textAlign: 'center' }}>
                            {cell.value !== undefined && cell.value !== null && cell.value !== ''
                              ? cell.render('Cell')
                              : '-'}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
            {!loading && rows.length === 0 && (
              <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[550px] flex items-center justify-center'>
                <span className='no-matching-records-message'>No matching records found</span>
              </div>
            )}
            {loading && (
              <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[550px] flex items-center justify-center'>
                <Loader />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  },
);

export default RealTimeDataTable;
